import { formatTimestamp, statusLabel } from '@saay/formatting';
import {
  AccordionPanel,
  CopyableIdentifier,
  DiffList,
  type DiffRow,
  FactList,
  Ltr,
} from './kit.js';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import type { AdminAuditEvent } from './api.js';
import { AUDIT_ACTION_LABELS } from './audit-actions.js';
import { StoredValue, storedFieldLabel } from './components/approval-view.js';
import { auditTrailPath } from './components/audit-trail-link.js';
import { shortReference } from './display.js';

const AUDIT_RESOURCE_LABELS: Readonly<Record<string, string>> = {
  APPROVAL_REQUEST: 'درخواست تأیید',
  BUYER: 'خریدار',
  IDENTITY_USER: 'کاربر',
  LEDGER_JOURNAL: 'سند دفترکل',
  MERCHANT: 'فروشگاه',
  PAYOUT_ACCOUNT: 'حساب تسویه',
  PHYSICAL_CARD_PRINT_BATCH: 'چاپ کارت',
  PLATFORM_ROLE_ASSIGNMENT: 'انتساب نقش',
};

/** Every named audit action, by its Persian name (the filter searches them). */
export const AUDIT_ACTION_OPTIONS: readonly { label: string; value: string }[] = Object.entries(
  AUDIT_ACTION_LABELS,
)
  .map(([value, label]) => ({ label, value }))
  .sort((left, right) => left.label.localeCompare(right.label, 'fa'));

export const AUDIT_RESOURCE_TYPE_OPTIONS: readonly { label: string; value: string }[] = [
  { label: 'فروشگاه', value: 'MERCHANT' },
  { label: 'خریدار', value: 'BUYER' },
  { label: 'درخواست تأیید', value: 'APPROVAL_REQUEST' },
  { label: 'حساب تسویه', value: 'PAYOUT_ACCOUNT' },
  { label: 'انتساب نقش', value: 'PLATFORM_ROLE_ASSIGNMENT' },
  { label: 'کاربر', value: 'IDENTITY_USER' },
  { label: 'سند دفترکل', value: 'LEDGER_JOURNAL' },
];

function valuesEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function collectFieldKeys(
  before: Readonly<Record<string, unknown>> | null,
  after: Readonly<Record<string, unknown>> | null,
): string[] {
  const keys = new Set<string>();
  if (before !== null) {
    for (const key of Object.keys(before)) keys.add(key);
  }
  if (after !== null) {
    for (const key of Object.keys(after)) keys.add(key);
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
}

export function buildAuditDiffRows(
  before: Readonly<Record<string, unknown>> | null,
  after: Readonly<Record<string, unknown>> | null,
): { readonly changed: readonly DiffRow[]; readonly unchanged: readonly DiffRow[] } {
  const changed: DiffRow[] = [];
  const unchanged: DiffRow[] = [];
  for (const key of collectFieldKeys(before, after)) {
    const previous = before?.[key];
    const next = after?.[key];
    const row: DiffRow = {
      changed: !valuesEqual(previous, next),
      key,
      label: storedFieldLabel(key),
      next: <StoredValue name={key} value={next} />,
      previous: <StoredValue name={key} value={previous} />,
    };
    if (row.changed) changed.push(row);
    else unchanged.push(row);
  }
  return { changed, unchanged };
}

export function auditResourceLabel(resourceType: string | null): string {
  if (resourceType === null) return '—';
  return AUDIT_RESOURCE_LABELS[resourceType] ?? resourceType;
}

export function AuditResourceCell({
  displayName,
  resourceId,
  resourceType,
}: {
  readonly displayName?: string | null;
  readonly resourceId: string | null;
  readonly resourceType: string | null;
}) {
  if (resourceType === null) {
    return <span className="admin-muted">—</span>;
  }
  const label = displayName ?? auditResourceLabel(resourceType);
  if (resourceId === null) {
    return <span>{label}</span>;
  }
  const path = resourceDetailPath(resourceType, resourceId);
  return (
    <span className="saay-cell-main">
      {path === undefined ? (
        <strong>{label}</strong>
      ) : (
        <Link to={path}>
          <strong>{label}</strong>
        </Link>
      )}
      <small>
        <Link to={auditTrailPath(resourceType, resourceId)}>سوابق</Link>
      </small>
    </span>
  );
}

function resourceDetailPath(resourceType: string, resourceId: string): string | undefined {
  switch (resourceType) {
    case 'MERCHANT':
      return `/merchants/${resourceId}`;
    case 'BUYER':
      return `/buyers/${resourceId}`;
    case 'APPROVAL_REQUEST':
      return `/approvals/${resourceId}`;
    case 'PLATFORM_ROLE_ASSIGNMENT':
      return '/platform-access';
    default:
      return undefined;
  }
}

export function AuditEventDiff({
  after,
  before,
}: {
  readonly after: Readonly<Record<string, unknown>> | null;
  readonly before: Readonly<Record<string, unknown>> | null;
}) {
  if (before === null && after === null) {
    return <p className="admin-muted">تغییری ثبت نشده است.</p>;
  }
  const { changed, unchanged } = buildAuditDiffRows(before, after);
  return (
    <>
      <DiffList items={changed} label="فیلدهای تغییرکرده" />
      {unchanged.length === 0 ? null : (
        <AccordionPanel
          items={[
            {
              content: <DiffList items={unchanged} label="فیلدهای بدون تغییر" />,
              key: 'unchanged',
              title: `فیلدهای بدون تغییر (${unchanged.length})`,
            },
          ]}
          label="فیلدهای بدون تغییر"
        />
      )}
    </>
  );
}

export function AuditEventMeta({ event }: { readonly event: AdminAuditEvent }) {
  const items: { key: string; label: ReactNode; value: ReactNode }[] = [
    {
      key: 'occurredAt',
      label: 'زمان',
      value: <time dateTime={event.occurredAt}>{formatTimestamp(event.occurredAt)}</time>,
    },
    ...(event.domain === null
      ? []
      : [
          {
            key: 'domain',
            label: 'حوزه',
            value: statusLabel('auditDomain', event.domain),
          },
        ]),
    ...(event.reason === null ? [] : [{ key: 'reason', label: 'دلیل', value: event.reason }]),
    {
      key: 'resource',
      label: 'مورد',
      value: (
        <AuditResourceCell
          displayName={event.resourceDisplayFa}
          resourceId={event.resourceId}
          resourceType={event.resourceType}
        />
      ),
    },
  ];
  const ids: { key: string; label: string; value: string }[] = [
    { key: 'action', label: 'کد عملیات', value: event.action },
  ];
  if (event.resourceId !== null) {
    ids.push({ key: 'resourceId', label: 'شناسهٔ مورد', value: event.resourceId });
  }
  if (event.requestId !== null) {
    ids.push({ key: 'requestId', label: 'کد پیگیری', value: event.requestId });
  }
  if (event.correlationId !== null) {
    ids.push({ key: 'correlationId', label: 'کد پیگیری مرتبط', value: event.correlationId });
  }
  if (event.merchantId !== null) {
    ids.push({ key: 'merchantId', label: 'شناسهٔ فروشگاه', value: event.merchantId });
  }
  if (event.branchId !== null) {
    ids.push({ key: 'branchId', label: 'شناسهٔ شعبه', value: event.branchId });
  }
  return (
    <>
      <FactList items={items} />
      {ids.length === 0 ? null : (
        <AccordionPanel
          items={[
            {
              content: (
                <dl className="saay-facts">
                  {ids.map((item) => (
                    <div className="admin-fact" key={item.key}>
                      <dt>{item.label}</dt>
                      <dd>
                        <CopyableIdentifier
                          label={<Ltr>{shortReference(item.value)}</Ltr>}
                          value={item.value}
                        />
                      </dd>
                    </div>
                  ))}
                </dl>
              ),
              key: 'ids',
              title: 'شناسه‌های فنی',
            },
          ]}
          label="شناسه‌های فنی رویداد"
        />
      )}
    </>
  );
}
